import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { FXAAShader } from 'three/examples/jsm/shaders/FXAAShader.js';

// rev: 2026-05-17 — force redeploy after smooth day/night palette
/**
 * Diorama post-processing stack.
 *
 *   RenderPass            base scene
 *   UnrealBloomPass       golden-hour glow on bright pixels
 *   TiltShiftPass         signature miniature-diorama blur (top + bottom)
 *   WarmGradeVignettePass cozy color lift + soft vignette
 *   FXAA                  smooth voxel-edge AA
 *   OutputPass            tonemap + gamma
 *
 * Why a single grade+vignette pass instead of two passes:
 *   - One full-screen draw is cheaper than two.
 *   - Vignette darkening should apply BEFORE the tonemap (in OutputPass),
 *     so it composes naturally with bloom rather than dimming a finished
 *     image.
 *
 * Tilt-shift implementation: a 9-tap Gaussian blur whose radius scales with
 * |y - focusY| / falloff. Sharp band stays sharp; top + bottom get
 * miniature-style blur. Adjustable via `setTiltShift(strength)` so plot
 * view can dampen the effect (the player is working those areas).
 */

/**
 * Screen-space god rays — classic radial-blur light shafts emanating from
 * the sun. Approach: for each pixel, march N samples along the line from
 * the pixel to the sun's screen position; samples that are bright (high
 * luminance) accumulate. The result is masked by the bright-pixel content,
 * so rays only stream out of the sun and nearby highlights — exactly the
 * "atmospheric scattering" look without depth-pass cost.
 *
 * Caller drives `sunScreenPos` (in [0,1] NDC-mapped) and `strength` every
 * frame from the day/night tick (no rays at night; soft rays at midday).
 */
const GodRaysShader = {
  uniforms: {
    tDiffuse:     { value: null as THREE.Texture | null },
    sunScreenPos: { value: new THREE.Vector2(0.5, 0.9) },
    decay:        { value: 0.95 },
    density:      { value: 0.9 },
    weight:       { value: 0.35 },
    threshold:    { value: 0.78 },
    strength:     { value: 0.5 },
  },
  vertexShader: /* glsl */`
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: /* glsl */`
    uniform sampler2D tDiffuse;
    uniform vec2 sunScreenPos;
    uniform float decay;
    uniform float density;
    uniform float weight;
    uniform float threshold;
    uniform float strength;
    varying vec2 vUv;

    // Take ONLY the bright tail of a sample so god rays stream from sky /
    // sun highlights, not from arbitrary midtone surfaces.
    vec3 brightOnly(vec3 c) {
      float l = dot(c, vec3(0.2126, 0.7152, 0.0722));
      float k = smoothstep(threshold, 1.0, l);
      return c * k;
    }

    void main() {
      vec4 src = texture2D(tDiffuse, vUv);
      if (strength < 0.01) {
        gl_FragColor = src;
        return;
      }
      // Vector from pixel toward sun screen-space position. Sample count
      // dropped from 36 to 22 — visually near-identical at typical ray
      // strengths, ~40% fewer texture lookups per pixel.
      vec2 dir = (sunScreenPos - vUv) * density;
      const int SAMPLES = 22;
      dir /= float(SAMPLES);
      vec2 uv = vUv;
      vec3 acc = brightOnly(src.rgb);
      float w = weight;
      for (int i = 0; i < SAMPLES; i++) {
        uv += dir;
        vec3 s = brightOnly(texture2D(tDiffuse, uv).rgb);
        acc += s * w;
        w *= decay;
      }
      // Tint the rays a touch warmer so they read as sunlight rather than
      // a generic blur, and add additively over the source.
      vec3 rays = acc * (1.0 / float(SAMPLES)) * strength;
      rays *= vec3(1.10, 1.02, 0.85);
      gl_FragColor = vec4(src.rgb + rays, src.a);
    }
  `,
};

const TiltShiftShader = {
  uniforms: {
    tDiffuse:   { value: null as THREE.Texture | null },
    resolution: { value: new THREE.Vector2(1, 1) },
    focusY:     { value: 0.50 },   // normalized [0,1] vertical screen pos
    focusBand:  { value: 0.18 },   // half-height of sharp band
    falloff:    { value: 0.45 },   // how fast blur grows past the band
    strength:   { value: 1.0 },    // global multiplier — 0 disables, 1 full
  },
  vertexShader: /* glsl */`
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: /* glsl */`
    uniform sampler2D tDiffuse;
    uniform vec2  resolution;
    uniform float focusY;
    uniform float focusBand;
    uniform float falloff;
    uniform float strength;
    varying vec2 vUv;

    // Distance-from-band → 0 at the focus stripe, 1 fully blurred.
    float blurAmount(float y) {
      float d = max(0.0, abs(y - focusY) - focusBand);
      return clamp(d / falloff, 0.0, 1.0) * strength;
    }

    // 5-tap Gaussian — symmetric weights, two passes (H + V) combined
    // via a cross-shaped kernel to keep this a single ShaderPass.
    // Reduced from 9-tap because the extra-wide taps contributed less
    // than 7% of the integral; 5-tap visually matches at 56% the sample
    // cost (10 lookups instead of 18). Slightly wider per-tap radius
    // compensates for the narrower kernel so the overall blur extent
    // matches what the player saw before.
    vec4 sampleBlur(vec2 uv, float radius) {
      vec2 texel = 1.0 / resolution;
      vec4 sum = vec4(0.0);
      // Renormalized weights for the [-2..2] subset of a sigma~1.4
      // gaussian — center heavier so silhouettes don't smear too far.
      const float w0 = 0.382925;
      const float w1 = 0.241732;
      const float w2 = 0.067040;
      // Step bumped 1.5× per-tap to keep the visible blur extent close
      // to the old 9-tap result.
      vec2 hx = vec2(texel.x * radius * 1.5, 0.0);
      sum += texture2D(tDiffuse, uv) * w0;
      sum += texture2D(tDiffuse, uv + hx * 1.0) * w1;
      sum += texture2D(tDiffuse, uv - hx * 1.0) * w1;
      sum += texture2D(tDiffuse, uv + hx * 2.0) * w2;
      sum += texture2D(tDiffuse, uv - hx * 2.0) * w2;
      vec4 v = vec4(0.0);
      vec2 hy = vec2(0.0, texel.y * radius * 1.5);
      v += texture2D(tDiffuse, uv) * w0;
      v += texture2D(tDiffuse, uv + hy * 1.0) * w1;
      v += texture2D(tDiffuse, uv - hy * 1.0) * w1;
      v += texture2D(tDiffuse, uv + hy * 2.0) * w2;
      v += texture2D(tDiffuse, uv - hy * 2.0) * w2;
      return (sum + v) * 0.5;
    }

    void main() {
      float b = blurAmount(vUv.y);
      if (b < 0.01) {
        gl_FragColor = texture2D(tDiffuse, vUv);
        return;
      }
      // Pixel radius: 0 at the band edge, ramps to ~6 at full blur. The
      // ramp is non-linear so the transition reads as a smooth falloff
      // rather than a hard ring.
      float radius = mix(0.0, 6.0, b * b);
      vec4 sharp = texture2D(tDiffuse, vUv);
      vec4 blur = sampleBlur(vUv, radius);
      gl_FragColor = mix(sharp, blur, b);
    }
  `,
};

const WarmGradeVignetteShader = {
  uniforms: {
    tDiffuse:    { value: null as THREE.Texture | null },
    warmth:      { value: 0.08 },   // 0..1 — pushes mids toward warm orange
    saturation:  { value: 1.10 },   // >1 boosts saturation
    lift:        { value: 0.02 },   // adds to shadows (cozier blacks)
    vignette:    { value: 0.35 },   // 0..1 — corner darkness
    vignetteSoft:{ value: 0.55 },   // larger = softer falloff
  },
  vertexShader: /* glsl */`
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: /* glsl */`
    uniform sampler2D tDiffuse;
    uniform float warmth;
    uniform float saturation;
    uniform float lift;
    uniform float vignette;
    uniform float vignetteSoft;
    varying vec2 vUv;

    vec3 toneWarm(vec3 c, float w) {
      // Push reds + yellows up, blues down. Sigmoid weight so highlights
      // and shadows are less affected than midtones (where cozy lives).
      float mid = smoothstep(0.05, 0.85, max(c.r, max(c.g, c.b)));
      vec3 warm = vec3(c.r * (1.0 + 0.15 * w), c.g * (1.0 + 0.06 * w), c.b * (1.0 - 0.10 * w));
      return mix(c, warm, mid);
    }

    void main() {
      vec4 src = texture2D(tDiffuse, vUv);
      vec3 c = src.rgb;

      // Lift the blacks slightly — cozy scenes never go true black.
      c += vec3(lift);

      // Warm grade.
      c = toneWarm(c, warmth);

      // Saturation boost — pulls voxel palette punchier without crushing.
      float l = dot(c, vec3(0.2126, 0.7152, 0.0722));
      c = mix(vec3(l), c, saturation);

      // Vignette — radial darkening from screen center.
      vec2 p = vUv - 0.5;
      float dist = length(p) * (1.0 + 0.25 * abs(p.x));   // slightly anamorphic
      float v = smoothstep(vignetteSoft, vignetteSoft - 0.35, dist);
      c *= mix(1.0 - vignette, 1.0, v);

      gl_FragColor = vec4(c, src.a);
    }
  `,
};

export class Postprocessing {
  private composer: EffectComposer;
  private renderPass: RenderPass;
  private godRays: ShaderPass;
  private bloomPass: UnrealBloomPass;
  private tiltShift: ShaderPass;
  private grade: ShaderPass;
  private fxaa: ShaderPass;

  // Toggle gates — passes stay in the composer chain so resize / pixel
  // ratio updates apply uniformly, but their `enabled` flag short-
  // circuits the GPU work. Set via setBloomEnabled / setTiltShiftEnabled
  // / setVignetteEnabled from Diorama when the Settings panel toggles.
  // bloomEnabled is tracked because setDayFactor needs to know whether
  // to keep pushing strength updates; tilt-shift's enabled flag lives
  // on the pass itself so we don't store a duplicate boolean here.
  private bloomEnabled = true;
  private vignetteEnabled = true;
  /** Saved day-curve so we can restore bloom strength when the player
   *  re-enables it after toggling off. `setDayFactor` runs every frame
   *  and overwrites `bloomPass.strength`; without remembering the day
   *  factor the strength after re-enable would be whatever was last
   *  used before disabling. */
  private lastDayFactor = 1;

  constructor(renderer: THREE.WebGLRenderer, scene: THREE.Scene, camera: THREE.Camera) {
    this.composer = new EffectComposer(renderer);
    this.composer.setPixelRatio(renderer.getPixelRatio());

    this.renderPass = new RenderPass(scene, camera);
    this.composer.addPass(this.renderPass);

    // God rays — radial blur of bright pixels toward the sun's screen
    // position. Has to come BEFORE bloom so the rays themselves bloom too.
    this.godRays = new ShaderPass(GodRaysShader);
    this.composer.addPass(this.godRays);

    // Subtle bloom — just enough to make sun-lit roofs and bright crops
    // glow at golden hour. Higher threshold so darks don't bleed.
    this.bloomPass = new UnrealBloomPass(
      new THREE.Vector2(window.innerWidth, window.innerHeight),
      0.35,   // strength
      0.55,   // radius
      0.82,   // threshold — only the bright pixels bloom
    );
    this.composer.addPass(this.bloomPass);

    this.tiltShift = new ShaderPass(TiltShiftShader);
    this.tiltShift.uniforms.resolution.value.set(window.innerWidth, window.innerHeight);
    this.composer.addPass(this.tiltShift);

    this.grade = new ShaderPass(WarmGradeVignetteShader);
    this.composer.addPass(this.grade);

    this.fxaa = new ShaderPass(FXAAShader);
    this.fxaa.uniforms.resolution.value.set(
      1 / window.innerWidth, 1 / window.innerHeight,
    );
    this.composer.addPass(this.fxaa);

    this.composer.addPass(new OutputPass());
  }

  render(_dt: number) {
    this.composer.render();
  }

  setSize(w: number, h: number) {
    // Refresh the composer's pixel ratio in case the player dragged the
    // window across monitors with different DPI scales (or a VM scale
    // change). Stale pixel ratio causes bloom/FXAA to shimmer.
    const renderer = (this.composer as unknown as { renderer: THREE.WebGLRenderer }).renderer;
    if (renderer) this.composer.setPixelRatio(renderer.getPixelRatio());
    this.composer.setSize(w, h);
    this.tiltShift.uniforms.resolution.value.set(w, h);
    this.fxaa.uniforms.resolution.value.set(1 / w, 1 / h);
    this.bloomPass.setSize(w, h);
  }

  /** Adjust tilt-shift for plot mode. In plot mode the player is working
   *  detail at sub-cell resolution; aggressive top/bottom blur would
   *  fight the work area. Damp it but keep a hint of miniature feel. */
  setPlotInView(inView: boolean) {
    if (inView) {
      this.tiltShift.uniforms.strength.value = 0.35;
      this.tiltShift.uniforms.focusBand.value = 0.28;
    } else {
      this.tiltShift.uniforms.strength.value = 1.0;
      this.tiltShift.uniforms.focusBand.value = 0.18;
    }
    this.grade.uniforms.vignette.value = this.currentVignetteAmount(inView);
  }

  /** Lower bloom + saturation when the sun is set so night still reads as
   *  night and doesn't get hue-pushed warm. Called from the day/night tick. */
  setDayFactor(d: number) {
    // d ∈ [0, 1] — 0 = midnight, 1 = noon.
    this.lastDayFactor = d;
    // Only push bloom strength when the bloom pass is active. If the
    // player has disabled bloom, leave strength at 0 so re-enabling
    // doesn't briefly flash the last computed value.
    if (this.bloomEnabled) this.bloomPass.strength = 0.18 + 0.22 * d;
    this.grade.uniforms.warmth.value = 0.04 + 0.10 * d;
    this.grade.uniforms.saturation.value = 1.04 + 0.10 * d;
  }

  /** Toggle the UnrealBloom pass. When off, the pass is skipped in
   *  the composer chain — saves ~10 internal blur passes per frame. */
  setBloomEnabled(b: boolean) {
    this.bloomEnabled = b;
    this.bloomPass.enabled = b;
    if (b) this.bloomPass.strength = 0.18 + 0.22 * this.lastDayFactor;
    else   this.bloomPass.strength = 0;
  }

  /** Toggle the tilt-shift miniature blur. */
  setTiltShiftEnabled(b: boolean) {
    this.tiltShift.enabled = b;
  }

  /** Toggle the warm grade + vignette. The grade contributes the warm
   *  tint that the diorama art is balanced against, so we only zero
   *  the vignette uniform when "off" rather than disabling the entire
   *  pass — the warmth stays. */
  setVignetteEnabled(b: boolean) {
    this.vignetteEnabled = b;
    this.grade.uniforms.vignette.value = b ? 0.35 : 0;
  }

  /** Re-apply the right vignette amount for the current plot-mode
   *  state. Called from setPlotInView so that the per-mode tuning
   *  (0.20 in plot vs 0.35 in overview) still respects the vignette
   *  on/off toggle. */
  private currentVignetteAmount(plotInView: boolean): number {
    if (!this.vignetteEnabled) return 0;
    return plotInView ? 0.20 : 0.35;
  }

  /** Place the god-rays focal point at the sun's screen position and ramp
   *  strength with sun height. `screenX/Y` are normalized [0,1] with origin
   *  at bottom-left. `strength` ∈ [0,1] — 0 disables (use when sun is below
   *  the horizon). */
  setSunScreen(screenX: number, screenY: number, strength: number) {
    (this.godRays.uniforms.sunScreenPos.value as THREE.Vector2).set(screenX, screenY);
    this.godRays.uniforms.strength.value = strength;
  }
}
